import React, { useState, useEffect } from "react";
import {
  Box,
  Stack,
  TextField,
  Button,
  InputLabel,
  MenuItem,
  FormControl,
  Select,
  Paper,
  Typography,
  Container,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import EditIcon from "@mui/icons-material/Edit";
import CancelIcon from "@mui/icons-material/Close";
import { LoadingButton } from "@mui/lab";
import axios from "axios";
import { toast } from "react-toastify";
import { API_ENDPOINT } from "../../constants/constants";
import { getHeader, getMultipartHeader, toastError } from "../../utils/util";
import useApiEp from "../hooks/useApiEp";

const accountTypes = ["SAVINGS", "CURRENT", "OD", "OTHERS"];

const PaymentDetailsForm = () => {
  // fetch existing details
  const { data, isLoading, isError, error, refetch } = useApiEp(
    "admin/get-payment-details"
  );

  // local form state
  const [form, setForm] = useState({
    upiId: "",
    bankName: "",
    branchName: "",
    ifsc: "",
    accountNo: "",
    accountHolder: "",
    accountType: accountTypes[0],
  });
  const [file, setFile] = useState(null);

  // UI state
  const [isEditing, setIsEditing] = useState(true); // default true until we know
  const [submitting, setSubmitting] = useState(false);

  // once data arrives, decide view/edit + preload values
  useEffect(() => {
    if (data) {
      setForm({
        upiId: data.upiId || "",
        bankName: data.bankName || "",
        branchName: data.branchName || "",
        ifsc: data.ifsc || "",
        accountNo: data.accountNo || "",
        accountHolder: data.accountHolder || "",
        accountType: data.accountType || accountTypes[0],
      });
      setIsEditing(false); // view mode first
    }
  }, [data]);

  const handleChange = (key) => (e) =>
    setForm({ ...form, [key]: e.target.value });

  const handleSubmit = async () => {
    if (!form.upiId.trim() || !form.accountNo.trim()) {
      toast.error("UPI ID and Account No are required");
      return;
    }

    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    if (file) fd.append("paymentQr", file);

    setSubmitting(true);
    try {
      await axios.post(
        `${API_ENDPOINT}admin/update-payment-details`,
        fd,
        getMultipartHeader()
      );
      toast.success("Payment details saved!");
      setIsEditing(false);
      setFile(null);
      refetch(); // refresh cached copy
    } catch (err) {
      toastError(err);
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) return null; // or skeleton
  if (isError)
    return (
      <Typography color="error">{error?.message || "Load failed"}</Typography>
    );

  return (
    <Container maxWidth="md">
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="h6" mb={2}>
          {isEditing ? "Edit / Add Payment Details" : "Saved Payment Details"}
        </Typography>

        <Stack spacing={2}>
          {/* ------- text fields ------- */}
          {[
            ["upiId", "UPI ID"],
            ["bankName", "Bank Name"],
            ["branchName", "Branch Name"],
            ["ifsc", "IFSC", { textTransform: "uppercase" }],
            ["accountNo", "Account Number"],
            ["accountHolder", "Account Holder Name"],
          ].map(([key, label, inputStyle]) => (
            <TextField
              key={key}
              label={label}
              value={form[key]}
              onChange={handleChange(key)}
              fullWidth
              disabled={!isEditing}
              inputProps={inputStyle && !isEditing ? {} : { style: inputStyle }}
            />
          ))}

          {/* Account type */}
          <FormControl fullWidth disabled={!isEditing}>
            <InputLabel id="acc-type-label">Account Type</InputLabel>
            <Select
              labelId="acc-type-label"
              label="Account Type"
              value={form.accountType}
              onChange={(e) =>
                setForm({ ...form, accountType: e.target.value })
              }
            >
              {accountTypes.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* QR image / upload */}
          {!isEditing && data?.picUrl && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Saved QR Image
              </Typography>
              <img
                src={data.picUrl}
                alt="Payment QR"
                style={{ maxWidth: 200, borderRadius: 8 }}
              />
            </Box>
          )}

          {isEditing && (
            <Button
              startIcon={<UploadFileIcon />}
              variant={file ? "contained" : "outlined"}
              component="label"
              sx={{ justifyContent: "flex-start" }}
            >
              {file ? file.name : "Upload Payment QR"}
              <input
                hidden
                accept="image/*"
                type="file"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </Button>
          )}

          {/* action buttons */}
          <Box textAlign="right">
            {isEditing ? (
              <>
                <Button
                  startIcon={<CancelIcon />}
                  sx={{ mr: 2 }}
                  onClick={() => {
                    // revert to existing data
                    if (data) {
                      setForm({
                        upiId: data.upiId || "",
                        bankName: data.bankName || "",
                        branchName: data.branchName || "",
                        ifsc: data.ifsc || "",
                        accountNo: data.accountNo || "",
                        accountHolder: data.accountHolder || "",
                        accountType: data.accountType || accountTypes[0],
                      });
                      setFile(null);
                      setIsEditing(false);
                    }
                  }}
                  disabled={!data}
                >
                  Cancel
                </Button>
                <LoadingButton
                  loading={submitting}
                  variant="contained"
                  onClick={handleSubmit}
                >
                  Save
                </LoadingButton>
              </>
            ) : (
              <Button
                startIcon={<EditIcon />}
                variant="contained"
                onClick={() => setIsEditing(true)}
              >
                Edit
              </Button>
            )}
          </Box>
        </Stack>
      </Paper>
    </Container>
  );
};

export default PaymentDetailsForm;
